import { Fragment, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FadeIn, Section } from "./ui";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import {
  getPosts,
  getPostCategory,
  getFeaturedImage,
  calculateReadTime,
  formatDate,
  type WPPost,
} from "../lib/wordpress";

function PostCardSkeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`flex flex-col h-full bg-brand-light/5 rounded-[2rem] overflow-hidden border border-brand-light/10 animate-pulse ${className}`}>
      <div className="bg-brand-light/10 aspect-video w-full" />
      <div className="p-8 flex flex-col gap-4">
        <div className="h-4 w-24 bg-brand-light/10 rounded-full" />
        <div className="h-6 w-3/4 bg-brand-light/10 rounded-full" />
        <div className="h-4 w-1/2 bg-brand-light/10 rounded-full" />
      </div>
    </div>
  );
}

function BlogCard({ post, index }: { post: WPPost; index: number }) {
  const image = getFeaturedImage(post);
  const readTime = calculateReadTime(post.content.rendered);
  const date = formatDate(post.date);

  return (
    <Link to={`/blog/${post.slug}/`} className="block h-full px-2 md:px-0">
      <article className="group cursor-pointer flex flex-col h-full bg-brand-light/5 rounded-[2rem] overflow-hidden border border-brand-light/10 hover:border-brand-accent/30 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(242,125,38,0.1)] transition duration-500">
        <div className="relative overflow-hidden aspect-[4/3]">
          <div className="absolute inset-0 bg-brand-dark/40 group-hover:bg-transparent transition-colors duration-500 z-10 mix-blend-multiply"></div>
          {image ? (
            <img
              src={image}
              alt={post.title.rendered}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition duration-700"
            />
          ) : (
            <div className="w-full h-full bg-brand-light/10" />
          )}
        </div>

        <div className="p-8 flex flex-col flex-grow bg-gradient-to-b from-brand-light/5 to-transparent">
          <div className="flex items-center gap-3 text-brand-light/50 text-sm font-medium mb-4">
            <span>{date}</span>
            <span className="w-1 h-1 rounded-full bg-brand-light/30"></span>
            <span>{readTime}</span>
          </div>
          <h3
            className="font-bold group-hover:text-brand-accent transition-colors leading-tight font-display mb-8 flex-grow text-2xl line-clamp-3"
            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
          />
          <div className="flex items-center gap-2 text-brand-accent font-bold uppercase tracking-widest text-sm mt-auto group-hover:text-brand-light transition-colors">
            Ler artigo <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </div>
        </div>
      </article>
    </Link>
  );
}

export function Blog() {
  const [posts, setPosts] = useState<WPPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    getPosts(6)
      .then(setPosts)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // Carousel Logic
  useEffect(() => {
    if (loading || error || posts.length === 0 || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % posts.length);
    }, 4000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loading, error, posts.length, isPaused]);

  return (
    <Section id="blog" className="pt-24 pb-32 overflow-hidden">
      <FadeIn>
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 border-b border-brand-light/20 pb-8">
          <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter">
            Insights <span className="font-highlight italic text-brand-accent lowercase font-normal">recentes</span>
          </h2>
          <div className="flex items-center gap-6 mt-6 md:mt-0">
            <Link to="/blog" className="flex items-center gap-2 text-brand-accent font-bold uppercase tracking-widest hover:text-brand-light transition-colors group">
              Ver todos <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </div>
      </FadeIn>

      {/* Grid for MD and LG */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <div key={i}>
            <PostCardSkeleton />
          </div>
        ))}

        {error && (
          <div className="col-span-full text-center text-brand-light/40 py-16">
            <p className="text-lg">Não foi possível carregar os posts.</p>
          </div>
        )}

        {!loading && !error && posts.map((post, index) => (
          <div key={post.id}>
            <FadeIn delay={index * 0.1}>
              <BlogCard post={post} index={index} />
            </FadeIn>
          </div>
        ))}
      </div>

      {/* Carousel for SM (Mobile) */}
      <div className="md:hidden relative px-4" onTouchStart={() => setIsPaused(true)} onTouchEnd={() => setIsPaused(false)}>
        {loading ? (
          <PostCardSkeleton />
        ) : error ? (
          <div className="text-center text-brand-light/40 py-16">
            <p className="text-lg">Não foi possível carregar os posts.</p>
          </div>
        ) : (
          <div className="relative h-full">
            <div className="overflow-hidden h-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="w-full cursor-grab active:cursor-grabbing"
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragEnd={(e, { offset }) => {
                    const swipe = offset.x;
                    if (swipe < -50) {
                      setActiveIndex((prev) => (prev + 1) % posts.length);
                    } else if (swipe > 50) {
                      setActiveIndex((prev) => (prev - 1 + posts.length) % posts.length);
                    }
                  }}
                >
                  <BlogCard post={posts[activeIndex]} index={activeIndex} />
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Dots */}
            <div className="flex justify-center mt-8">
              {posts.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveIndex(idx);
                    setIsPaused(true);
                  }}
                  className="p-2.5 flex items-center justify-center"
                  aria-label={`Ir para post ${idx + 1}`}
                >
                  <span
                    className={`block h-2 rounded-full transition duration-300 ${activeIndex === idx ? "w-6 bg-brand-accent" : "w-2 bg-brand-light/20"}`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}
